import React, { useState, useEffect } from "react";
import {
  Box,
  HStack,
  Text,
  IconButton,
  Spacer,
  VStack,
  Icon,
  Avatar,
  Button,
  Input,
  Flex,
  AlertDialog,
  AlertDialogOverlay,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogCloseButton,
  useDisclosure,
} from "@chakra-ui/react";
import { EditIcon, AddIcon } from "@chakra-ui/icons";
import {
  FaTrash,
  FaMapMarkerAlt,
  FaHotel,
  FaCar,
  FaUtensils,
  FaShoppingBag,
  FaGlassCheers,
  FaGift,
  FaTshirt,
  FaCamera,
  FaTools,
  FaPlane,
} from "react-icons/fa"; // Importing all the icons at once
import axiosInstance from "../../helpers/axiosInstance";
import { useCurrency } from "../CurrencyExchange/CurrencyContext";
import BudgetManager from "./BudgetManager";
import moment from "moment"; // For date formatting and sorting
import Translate from "../../components/LanguageTranslation/Translate";
import { categoryIcons } from "./BudgetPlanner";

const ExpensesList = ({
  expenses,
  setExpenses,
  selectedTripId,
  onOpen,
  setNewExpenseName,
  setNewCost,
  setSelectedCurrency,
  setSelectedCategory,
  setSelectedDate,
  setSelectedLocation,
  setEditingExpenseIndex,
  setSelectedColor,
  setIsShared,
  selectedTrip,
  totalBudget, setTotalBudget,
  setOldExpenseName
}) => {
  const { convertCurrency, currency } = useCurrency();
  const {
    isOpen: isAlertOpen,
    onOpen: onOpenAlert,
    onClose: onCloseAlert
  } = useDisclosure();
  const [expenseToDelete, setExpenseToDelete] = useState(null);
  const cancelRef = React.useRef();

  useEffect(() => {
    const fetchExpenses = async () => {
      try {
        const response = await axiosInstance.get(
          `/budget_planner/${selectedTripId}/expenses`
        );
        // console.log("fetchExpenses", response.data);
        setExpenses(response.data);
      } catch (error) {
        console.error("Error fetching expenses:", error);
      }
    };

    if (selectedTripId) {
      fetchExpenses();
    }
  }, [selectedTripId]);

  // Calculate total expenses
  const total = expenses.reduce((acc, categoryWithExpenses) => {
    return (
      acc +
      categoryWithExpenses.expenses.reduce(
        (categoryAcc, expense) => categoryAcc + parseFloat(expense.cost),
        0
      )
    );
  }, 0);

  // Helper function to group expenses by date
  const groupExpensesByDate = (expensesList) => {
    // console.log("expensesList", expensesList);
    return expensesList.reduce((groupedExpenses, categoryWithExpenses) => {
      categoryWithExpenses.expenses.forEach((expense) => {
        const expenseDate = moment(expense.expense_date).format("YYYY-MM-DD"); // Group by date
        if (!groupedExpenses[expenseDate]) {
          groupedExpenses[expenseDate] = [];
        }
        groupedExpenses[expenseDate].push({
          ...expense,
          icon: categoryWithExpenses.icon,
          category: categoryWithExpenses.category_name,
          editable: categoryWithExpenses.editable, // Add the editable field from category
        });
      });
      // console.log("groupedExpenses", groupedExpenses);
      return groupedExpenses;
    }, {});
  };

  // Group expenses by date
  const groupedExpenses = groupExpensesByDate(expenses);

  // Sort dates in descending order (latest first)
  const sortedDates = Object.keys(groupedExpenses).sort(
    (a, b) => new Date(b) - new Date(a)
  );

  const openEditExpenseModal = (expenseIndex, expense, category) => {
    setOldExpenseName(expense.expense_name); // Store the old expense name
    setNewExpenseName(expense.expense_name);
    setNewCost(expense.true_cost);
    setSelectedCurrency(expense.expense_currency);
    setSelectedCategory(category); // Ensure this is properly set
    setSelectedDate(expense.expense_date);
    setSelectedLocation(expense.expense_location);
    setEditingExpenseIndex(expenseIndex);
    setSelectedColor(expense.color || "#ff0000");
    if (expense.expense_user === "shared") {
      setIsShared("shared");
    } else {
      setIsShared("personal");
    }
    onOpen(); // Open the modal
  };

  const deleteExpense = async (expenseName, category) => {
    try {
      await axiosInstance.delete(
        `/budget_planner/${selectedTripId}/delete_expense`,
        {
          data: { category, expense_name: expenseName },
        }
      );
      const response = await axiosInstance.get(
        `/budget_planner/${selectedTripId}/expenses`
      );
      setExpenses(response.data);
    } catch (error) {
      console.error("Error deleting expense:", error);
    }
  };

  // Handler to open the alert dialog
  const handleDeleteClick = (expense) => {
    setExpenseToDelete(expense);
    onOpenAlert();
  };

  // Handler for confirming deletion
  const handleConfirmDelete = async () => {
    if (expenseToDelete) {
      await deleteExpense(expenseToDelete.expense_name, expenseToDelete.category);
      setExpenseToDelete(null);
      onCloseAlert();
    }
  };

  return (
    <Box
      flex="5"
      w={{ base: "100%", md: "80%", lg: "75%" }}
      h={{ base: "auto", md: "100vh", lg: "100vh" }}
      display="flex"
      flexDirection="column"
      p={{ base: 0, md: 2, lg: 2 }}
      bg="#FAFDFC"
      overflowY={{ base: "none", md: "auto", lg: "auto" }}
      sx={{
        "&::-webkit-scrollbar": {
          display: "none",
        },
        msOverflowStyle: "none",
        scrollbarWidth: "none",
      }}
    >
      <BudgetManager
        total={total}
        currency={currency}
        convertCurrency={convertCurrency}
        selectedTripId={selectedTripId}
        totalBudget={totalBudget}
        setTotalBudget={setTotalBudget}
      />
      <Box
        mt={2}
        p={4}
        maxH={{ base: "auto", md: "90vh", lg: "90vh" }}
        bg="white"
        borderRadius="xl"
        overflowY={{ base: "none", md: "auto", lg: "auto" }}
        sx={{
          "&::-webkit-scrollbar": {
            display: "none",
          },
          msOverflowStyle: "none",
          scrollbarWidth: "none",
        }}
      >
        {sortedDates.map((date, idx) => (
          <Box key={date}>
            {/* Date header row */}
            <Flex align="center" justifyContent="space-between" mb={2}>
              <Text fontSize="lg" fontWeight="bold" mb={0}>
                {moment(date).format("dddd, Do MMMM YYYY")}
              </Text>

              {/* Show the button only for the first date group */}
              {idx === 0 && (
                <Button
                // display={{base: "block", md: "none"}}
                  ml={2}
                  bg="#286328"
                  size="sm"
                  color="white"
                  _hover="none"
                  borderRadius="full"
                  leftIcon={<AddIcon />}
                  onClick={onOpen}
                  isDisabled={!selectedTrip}
                >
                  <Translate text="Add Expense" />
                </Button>
              )}
            </Flex>

            {/* Expenses under this date */}
            {groupedExpenses[date].map((expense, expIndex) => {
              const convertedCost = parseFloat(
                convertCurrency(expense.cost, "USD")
              );
              const categoryData = categoryIcons[expense.category] || {};

              return (
                <Box
                  key={expIndex}
                  p={4}
                  bg="white"
                  rounded="lg"
                  shadow="lg"
                  mb={6}
                >
                  <HStack justify="space-between" alignItems="center">
                    <HStack>
                      <Avatar
                        icon={
                          <Icon
                            as={
                              categoryIcons[expense.icon]?.icon || FaShoppingBag
                            }
                          />
                        }
                        bg={expense.color || "#000000"}
                        size="sm"
                        mr={2}
                      />
                      <VStack spacing={0} align="start">
                        <Text fontWeight="bold" mb={0}>
                          <Translate text={expense.expense_name} />
                        </Text>
                        <Text fontSize="sm" color="gray.500" mb={0}>
                          <Translate text={expense.category} />
                        </Text>
                      </VStack>
                    </HStack>
                    <Spacer />
                    <Text fontWeight="bold" mr={2} mb={0}>
                      {currency} {convertedCost?.toFixed(2) ?? "0.00"}
                    </Text>

                    <IconButton
                      icon={<EditIcon />}
                      size="xs"
                      onClick={() =>
                        openEditExpenseModal(
                          expIndex,
                          expense,
                          expense.category
                        )
                      }
                    />
                    {expense.editable !== false && (
                      <IconButton
                        icon={<FaTrash />}
                        size="xs"
                        onClick={() => handleDeleteClick(expense)}
                      />
                    )}
                  </HStack>
                </Box>
              );
            })}
          </Box>
        ))}

        <HStack justify="right" pt={2}>
          <Text fontWeight="bold">
            <Translate text="Total" />
          </Text>
          <Text fontWeight="bold">=</Text>
          <Text fontWeight="bold">
            {currency}{" "}
            {convertCurrency(total, "USD") &&
            !isNaN(convertCurrency(total, "USD"))
              ? Number(convertCurrency(total, "USD")).toFixed(2)
              : "0.00"}
          </Text>
        </HStack>
      </Box>
      {/* AlertDialog for delete confirmation */}
      <AlertDialog
        isOpen={isAlertOpen}
        leastDestructiveRef={cancelRef}
        onClose={onCloseAlert}
        size={{base: "sm", md: "md"}}
        isCentered
      >
        <AlertDialogOverlay />
        <AlertDialogContent>
          <AlertDialogHeader fontSize="lg" fontWeight="bold">
            <Translate text="Delete Expense" />
          </AlertDialogHeader>
          <AlertDialogCloseButton />
          <AlertDialogBody>
            <Translate text="Are you sure you want to delete this expense? This action cannot be undone." />
          </AlertDialogBody>
          <AlertDialogFooter>
            <Button ref={cancelRef} onClick={onCloseAlert}>
              <Translate text="Cancel" />
            </Button>
            <Button colorScheme="red" onClick={handleConfirmDelete} ml={3}>
              <Translate text="Delete" />
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Box>
  );
};

export default ExpensesList;
